import { useState } from 'react';
import { KeyRound, Plus, Trash2, UserPlus } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { CopyButton } from '@/components/ui/copy-button';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { classifyError } from '@/lib/errors';
import { notifyMutationError } from '@/lib/errors';
import { useNotification } from '@/context/NotificationProvider';
import {
  useCertificadosServico,
  useRetirarCertificadoServico,
} from '@/hooks/useCertificadosServico';
import type { CertificadoServicoItem } from '@/api/certificadoServico';
import { CadastrarServicoDialog } from './CadastrarServicoDialog';
import { DesignarServicoDialog } from './DesignarServicoDialog';

function mascararCpf(cpf: string): string {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return cpf;
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
}

function formatarData(iso: string | null): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('pt-BR');
}

function StatusBadge({ item }: { item: CertificadoServicoItem }) {
  if (item.usavel) return <Badge>Usável</Badge>;
  if (!item.certificado_ativo) return <Badge variant="destructive">Sem certificado ativo</Badge>;
  if (!item.conexao_pdpj) return <Badge variant="destructive">Sem conexão PDPJ</Badge>;
  return <Badge variant="outline">Inativo</Badge>;
}

export default function CertificadosServicoPage() {
  const { showSuccess, showError } = useNotification();
  const { data, isLoading, error } = useCertificadosServico();
  const retirar = useRetirarCertificadoServico();

  const [designarOpen, setDesignarOpen] = useState(false);
  const [cadastrarOpen, setCadastrarOpen] = useState(false);
  const [retirarAlvo, setRetirarAlvo] = useState<CertificadoServicoItem | null>(null);

  const confirmarRetirar = () => {
    if (!retirarAlvo) return;
    retirar.mutate(retirarAlvo.cpf, {
      onSuccess: () => {
        showSuccess('Certificado de serviço retirado.');
        setRetirarAlvo(null);
      },
      onError: (err) => notifyMutationError(err, showError),
    });
  };

  const itens = data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Certificados de Serviço"
        subtitle="Pool usado para consulta pública (Res. 121) de usuários sem certificado próprio. Nunca é usado para peticionar."
        icon={<KeyRound />}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setDesignarOpen(true)}>
              <UserPlus className="h-4 w-4 mr-1.5" />
              Designar por CPF
            </Button>
            <Button onClick={() => setCadastrarOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              Cadastrar novo
            </Button>
          </div>
        }
      />

      <Alert>
        <AlertDescription>
          Estes certificados autenticam consultas na PDPJ em nome de um advogado real. Use apenas
          certificados institucionais ou com consentimento explícito. Usuários sem certificado
          próprio veem somente dados públicos (movimentos e atos judiciais).
        </AlertDescription>
      </Alert>

      {isLoading && <Spinner size={32} label="Carregando certificados de serviço" />}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {!isLoading && !error && itens.length === 0 && (
        <EmptyState
          icon={<KeyRound />}
          title="Nenhum certificado de serviço"
          description="Designe um CPF existente ou cadastre um novo para habilitar a consulta pública."
        />
      )}

      {itens.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titular</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Último uso</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((item) => (
                  <TableRow key={item.cpf}>
                    <TableCell>
                      <div className="font-medium">{item.titular_nome ?? '—'}</div>
                      {item.rotulo && (
                        <div className="text-xs text-muted-foreground">{item.rotulo}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1">
                        {mascararCpf(item.cpf)}
                        <CopyButton text={item.cpf} label="Copiar CPF" />
                      </span>
                    </TableCell>
                    <TableCell>{formatarData(item.valido_ate)}</TableCell>
                    <TableCell>
                      <StatusBadge item={item} />
                    </TableCell>
                    <TableCell>{formatarData(item.ultimo_uso_em)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRetirarAlvo(item)}
                        aria-label="Retirar certificado de serviço"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <DesignarServicoDialog open={designarOpen} onOpenChange={setDesignarOpen} />
      <CadastrarServicoDialog open={cadastrarOpen} onOpenChange={setCadastrarOpen} />

      <ConfirmDialog
        open={retirarAlvo !== null}
        title="Retirar certificado de serviço?"
        message={
          retirarAlvo
            ? `O CPF ${mascararCpf(retirarAlvo.cpf)} deixa de servir consultas públicas. O certificado em si não é removido.`
            : ''
        }
        confirmLabel="Retirar"
        confirmColor="error"
        loading={retirar.isPending}
        onConfirm={confirmarRetirar}
        onCancel={() => setRetirarAlvo(null)}
      />
    </div>
  );
}
